import type { playerFiltersEn } from './en.ts'

/**
 * Russian locale catalog for the player-filters sub-domain.
 *
 * Source of truth: `playerFiltersEn` (`./en.ts`). Russian must satisfy
 * `typeof playerFiltersEn` exactly so the type system catches every
 * structural drift between the two catalogs.
 *
 * Translation notes:
 *   - "Tag assignment" / "Group roster" / "Board view" are the three
 *     drawer eyebrows; each maps to a domain noun in Russian.
 *   - Player names, weapon names, tag names, group labels and room names
 *     pass through verbatim as arguments — they are persisted data.
 *   - The pluralisation rules for "result(s)" / "player(s)" follow the
 *     standard Russian count forms (one, 2-4, 5+).
 */
export const playerFiltersRu: typeof playerFiltersEn = {
  controls: {
    modeHeading: 'Поведение доски',
    modeDescription:
      'Выберите, скрывать ли игроков, не подходящих под фильтры, или оставлять их видимыми, но приглушёнными.',
    modeGroupLabel: 'Режим фильтра игроков',
    modeFilter: 'Скрывать',
    modeHighlight: 'Подсвечивать',
    nameHeading: 'Имя игрока',
    nameDescription: 'Ищет по игровому имени и Discord-имени.',
    nameInputLabel: 'Имя игрока',
    nameInputPlaceholder: 'Введите имя игрока',
    weaponsHeading: 'Оружия',
    weaponsDescription:
      'Совпадение с видимым текущим билдом. Другие личные и гильдейские билды показаны отдельно.',
    tagsHeading: 'Теги',
    tagsDescription: 'Совпадение с любым выбранным тегом гильдии.',
    noGuildTags: 'В этой гильдии пока нет тегов игроков.',
    clearFilters: 'Сбросить фильтры',
  },
  drawer: {
    eyebrow: {
      tag: 'Назначение тегов',
      group: 'Состав группы',
      board: 'Вид доски',
    },
    status: {
      matchCount: (matching: number, total: number) =>
        `${matching} из ${total} игроков подходят`,
      hintNoFilters: 'Добавьте имя, оружие или тег, чтобы изменить вид доски.',
      hintOtherBuilds:
        'Доска совпадает с видимым текущим билдом. Игроки с этими оружиями только в другом бидде показаны ниже.',
      hintFilterMode:
        'Показаны только подходящие игроки. Перетаскивание приостановлено, пока скрытые игроки остаются в порядке групп.',
      hintHighlightMode: 'Подходящие игроки остаются в полном контрасте; остальные карточки приглушены.',
    },
    selectorHeading: (target: string) => `Выберите игроков для ${target}`,
    selectorHintTag: 'Отметка назначает тег; снятие отметки удаляет его у игрока.',
    selectorHintGroup:
      'Снятие отметки возвращает записавшегося игрока в «Записались», а остальных — в «Прочие».',
    selectorMissingTag: 'Этот тег больше не доступен. Закройте панель и попробуйте снова.',
    selectorMissingGroup: 'Эта группа больше не доступна. Закройте панель и попробуйте снова.',
    titleFallback: 'Фильтры игроков',
    titleForTag: (name: string) => `${name} · Игроки`,
    titleForGroup: (name: string) => `${name} · Игроки`,
    currentBuildHeading: 'Текущий билд',
    currentBuildDescription: 'Игроки, у которых выбранное оружие сейчас показано на карточке.',
    otherBuildsHeading: 'Другие личные и гильдейские билды',
    otherBuildsDescription: 'Игроки с выбранным оружием вне их текущего билда.',
    matchingHeading: 'Подходящие игроки',
    resultCountSingular: (count: number) => `${count} результат`,
    resultCountPlural: (count: number) =>
      `${count} ${count >= 2 && count <= 4 ? 'результата' : 'результатов'}`,
  },
  resultList: {
    emptyCurrentWeapon: 'Ни один игрок не использует выбранное оружие в текущем бидде.',
    emptyOtherBuildWeapon:
      'Ни один игрок не имеет выбранное оружие в другом личном или гильдейском бидде.',
    emptyNoFilters: 'Ни один игрок не подходит под эти фильтры.',
    listLabelCurrentBuildSelector: (target: string) =>
      `Игроки текущего билда для ${target}`,
    listLabelCurrentBuildFilter: 'Игроки, подходящие по их текущему билду',
    listLabelOtherBuildSelector: (target: string) =>
      `Игроки других билдов для ${target}`,
    listLabelOtherBuildFilter: 'Игроки, подходящие по другому личному или гильдейскому билду',
    listLabelSelectorOnly: (target: string) => `Игроки для ${target}`,
    sourceLabelPlayerBuild: 'Личный билд',
    sourceLabelGuildBuild: 'Гильдейский билд',
    buildChoiceGuild: (index: number) => `Гильдейский ${String(index + 1)}`,
    buildChoicePlayerDefault: 'Личный 1 · По умолчанию',
    buildChoicePlayer: (index: number) => `Личный ${String(index + 1)}`,
    preferredBuildBadge: 'Будет использован',
    willUseChoiceHint: 'Предпочтительный выбор для этого назначения.',
    noWeapons: 'Нет оружий',
    overflowTags: (extra: number) => `+${String(extra)}`,
    fallbackGroupUnassigned: 'Без группы',
    fallbackGroupLabel: (index: number) => `Группа ${String(index + 1)}`,
    groupLabelWithRoom: (room: string, group: string) => `${room} · ${group}`,
    playerToggleTitlePending: 'Ждём подтверждения изменений на доске',
    playerToggleTitleFallbackSigned: 'Это резервная группа для записавшихся игроков',
    playerToggleTitleFallbackOther: 'Это резервная группа для незаписавшихся игроков',
    pendingStatusTag: 'Обновление тега…',
    pendingStatusMove: 'Перемещение…',
    rowTitleCurrent: (player: string) => `${player} · Текущий билд`,
    rowTitleLabels: (player: string, labels: string) => `${player} · ${labels}`,
    fallbackGroupLabelProp: 'группа',
    fallbackTagLabelProp: 'тег',
    playerToggleAriaAssignTag: (tagLabel: string, playerName: string) =>
      `Назначить тег ${tagLabel} игроку ${playerName}`,
    playerToggleAriaRemoveTag: (tagLabel: string, playerName: string) =>
      `Удалить тег ${tagLabel} у игрока ${playerName}`,
    playerToggleAriaMoveToGroup: (playerName: string, groupLabel: string) =>
      `Переместить ${playerName} в группу ${groupLabel}`,
    playerToggleAriaRemoveFromGroup: (playerName: string, groupLabel: string) =>
      `Удалить ${playerName} из группы ${groupLabel}`,
  },
}